/* eslint-disable react-hooks/exhaustive-deps */
import { Grid, makeStyles } from '@material-ui/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import i18n from 'i18n';
import { getAllClientsRequest } from 'modules/clients/actions';
import GetAppIcon from '@material-ui/icons/GetApp';
import {
  ListClientsAppsFields,
  ListClientsPlatformAppsProps,
} from 'modules/environments/interfaces';

import React from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import ApplicationState from 'store/interfaces';
import { getClientsApplicationsPlatformRequest } from 'modules/environments/actions';
import ScreenStatesApplications from 'containers/Clients/ApplicationsClients/ScreenStatesApplications';
import Button from 'components/Button';
import palette from 'theme/palette';
import { modalClose } from 'modules/modal/actions';
import { messagesRequest } from 'modules/messages/actions';
import { useTableFilters } from 'hooks/useTableFilters';
import { SearchBar } from 'components/SearchBar';
import styles from './styles';

const useStyles = makeStyles(styles);

interface Props {
  appCode: string;
  appName: string;
  environmentName: string | undefined;
  isLoadingClientsApps: boolean;
  clientsPlatformApplicationsList: ListClientsPlatformAppsProps;
  getClientsApplicationsPlatformRequest: (props) => void;
  modalClose: () => void;
}

const ListApplicationClients = ({
  appCode,
  appName,
  environmentName,
  isLoadingClientsApps,
  clientsPlatformApplicationsList,
  getClientsApplicationsPlatformRequest,
  modalClose,
}: Props) => {
  const classes = useStyles();

  const {
    query,
    searchValue,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters();
  const { id } = useParams<{ id: string }>();

  const getClientsApplicationsPlatform = () => {
    getClientsApplicationsPlatformRequest({
      id,
      query,
      code: appCode,
      state: 'attached',
    });
  };

  React.useEffect(() => {
    getClientsApplicationsPlatform();
  }, [getClientsApplicationsPlatformRequest, id, query, appCode]);

  const mapState = (state: 'attached' | 'detached'): string => {
    const options = {
      attached: i18n.t('ATTACHED_KEY'),
      detached: i18n.t('DETACHED_KEY'),
    };

    return options[state] || state;
  };

  const getClientsListPDF: Array<[
    string,
    string
  ]> = clientsPlatformApplicationsList?.results.map(item => [
    item?.client?.name,
    mapState(item?.state),
  ]);

  // eslint-disable-next-line new-cap
  const doc = new jsPDF();

  const generatePDFButton = () => {
    doc.setFontSize(16);
    doc.text(
      [
        '',
        `${i18n.t('ENVIRONMENT_NAME_KEY')}: ${environmentName}`,
        `${i18n.t('APPLICATIONS_NAME_KEY')}: ${appName}`,
        `${i18n.t('PDF_GENERATED_AT_KEY')}: ${new Date().toLocaleString(
          'pt-BR',
          {
            hour12: false,
          }
        )}`,
        `${i18n.t('CLIENTS_PAGE.LIST_CLIENTS_TITLE_KEY')}:`,
        '',
      ],
      10,
      10,
      { lineHeightFactor: 1.5, maxWidth: 200 }
    );
    autoTable(doc, {
      head: [[`${i18n.t('CLIENT_NAME_KEY')}`, 'Status']],
      margin: { top: 50 },
      styles: { lineWidth: 0.3, lineColor: palette.borderGray, fontSize: 11 },
      headStyles: {
        fillColor: palette.deepBlue,
        textColor: palette.offWhite,
        fontSize: 11,
      },
      body: getClientsListPDF,
    });
    doc.save(`${appName}.pdf`);
  };

  return (
    <Grid container spacing={2} className={classes.container}>
      <Grid container>
        <SearchBar
          id="search-client"
          isLoading={isLoadingClientsApps}
          searchValue={searchValue}
          placeholder={i18n.t('SEARCH_CLIENT_KEY')}
          handleSearch={handleSearch}
          setSearchValue={setSearchValue}
        />
        <TableItems
          header={
            <>
              <TableCell className={classes.tableRow}>
                {i18n.t('CLIENT_NAME_KEY')}
              </TableCell>
              <TableCell centerTitle className={classes.state}>
                {i18n.t('STATUS_KEY')}
              </TableCell>
            </>
          }
          renderItems={(item: ListClientsAppsFields, index) => (
            <TableRow key={index}>
              <TableCell>{item?.client.name}</TableCell>
              <TableCell textAlignCenter centerTitle>
                <ScreenStatesApplications type={item?.state} />
              </TableCell>
            </TableRow>
          )}
          colCount={2}
          isLoading={isLoadingClientsApps}
          data={clientsPlatformApplicationsList}
          onChangeLimit={handleLimitChange}
          onChangePage={handlePageChange}
          notFoundText={i18n.t('NO_CLIENT_KEY')}
        />
      </Grid>
      <Grid container justify="flex-end" className={classes.buttons}>
        <Button
          id="generate-pdf-button"
          submitButton
          startIcon={<GetAppIcon />}
          onClick={() => {
            generatePDFButton();
            modalClose();
            messagesRequest({
              type: 'success',
              message: i18n.t('PDF_GENERATED_SUCCESSFULLY_KEY'),
            });
          }}
        >
          {i18n.t('GENERATE_PDF')}
        </Button>
        <Button id="cancel-button" variant="outlined" onClick={modalClose}>
          {i18n.t('CLOSE_KEY')}
        </Button>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  environments: {
    isLoadingClientsApps,
    clientsPlatformApplicationsList,
    environmentSelected,
  },
}: ApplicationState) => ({
  isLoadingClientsApps,
  clientsPlatformApplicationsList,
  environmentSelected,
});

export default connect(mapStateToProps, {
  getClientsApplicationsPlatformRequest,
  getAllClientsRequest,
  modalClose,
})(ListApplicationClients);
